<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { Eye, Copy, Loader2 } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import type { TForm } from '@/types/generated/types.gen'

  const props = defineProps<{
    modelValue: boolean
    title: string
    description: string
    items: any[]
    isLoading: boolean
    searchPlaceholder?: string
  }>()

  const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'select-item': [item: any]
    'preview-item': [item: any]
  }>()

  const search = ref('')

  const filteredItems = computed(() => {
    if (!search.value) return props.items
    const searchQuery = search.value.toLowerCase()
    return props.items.filter((item) =>
      (item.title || item.name || (item as TForm).document_name || '')
        .toLowerCase()
        .includes(searchQuery)
    )
  })

  const close = () => {
    emit('update:modelValue', false)
    search.value = ''
  }

  const handleSelect = (item: any) => {
    emit('select-item', item)
  }

  const handlePreview = (item: any) => {
    emit('preview-item', item)
  }
</script>

<template>
  <Dialog
    :open="modelValue"
    @update:open="close"
  >
    <DialogContent class="sm:max-w-[800px]">
      <div class="flex justify-between">
        <DialogHeader>
          <DialogTitle>{{ title }}</DialogTitle>
          <DialogDescription>
            {{ description }}
          </DialogDescription>
        </DialogHeader>
      </div>

      <!-- Search Input -->
      <div class="relative w-full">
        <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <svg
            class="size-4 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        <input
          v-model="search"
          type="text"
          class="block w-full rounded-lg border-gray-300 ps-10 focus:border-purple-500 focus:ring-purple-500"
          :placeholder="searchPlaceholder"
        />
      </div>

      <!-- Items List -->
      <div class="max-h-[400px] divide-y divide-gray-200 overflow-y-auto py-4">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-4"
        >
          <Loader2 class="h-6 w-6 animate-spin text-gray-500" />
        </div>
        <div
          v-else-if="filteredItems.length === 0"
          class="flex items-center justify-center py-4 text-gray-500"
        >
          No results found
        </div>
        <div
          v-for="item in filteredItems"
          v-else
          :key="item.uuid"
          class="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
        >
          <div class="min-w-0 flex-1">
            <h3 class="truncate pr-4 font-medium text-gray-900">
              {{ item.title || item.name || item.document_name }}
            </h3>
            <div
              v-if="item.law_field || item.law_topic"
              class="mt-1 text-xs text-gray-500"
            >
              <span v-if="item.law_field">{{ item.law_field }}</span>
              <span v-if="item.law_field && item.law_topic">/</span>
              <span v-if="item.law_topic">{{ item.law_topic }}</span>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              class="gap-1 text-gray-600 hover:text-gray-900"
              @click="() => handlePreview(item)"
            >
              <Eye class="h-4 w-4" />
              Vorschau
            </Button>
            <Button
              variant="default"
              size="sm"
              class="gap-1 bg-purple-600 text-white hover:bg-purple-700"
              @click="() => handleSelect(item)"
            >
              <Copy class="h-4 w-4" />
              Link kopieren
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
